import { types, getParent, flow } from 'mobx-state-tree';
import Template from '../../models/SettingsStore/Template';
import ListingOptions from '../../models/SettingsStore/ListingOptions';
import { upperThreeToTwo } from '../../constants/countries';

const URL = 'seller-account/v1/listing-settings';

const Listing = types
  .model('Listing', {
    templateList: types.optional(types.array(Template), []),
    listingOptions: types.optional(ListingOptions, {}),
    sourceMarketplaceId: types.maybe(types.string),
    sellerAccountCode: types.maybe(types.string),
    destinationMarketplaceId: types.maybe(types.string),
    testNumber: types.maybe(types.string),
    loading: false,
  })
  .views(self => ({
    get transport5007() {
      return getParent(self).transport5007;
    },
    get getTemplateList() {
      return self.templateList;
    },
    get noListingOptions() {
      return self.listingOptions.isDefault;
    },
  }))
  .actions(self => {
    const updateTestNumber = number => {
      self.testNumber = number;
    };
    const setSellerAccountCode = code => {
      self.sellerAccountCode = code;
    };
    const setSourceMarketplaceid = id => {
      self.sourceMarketplaceId = id;
    };
    const setDestMarketplaceid = id => {
      self.destinationMarketplaceId = id;
    };

    const scanUserByToken = flow(function* scanUserByToken(sellerAccountId) {
      try {
        yield self.transport5007.post(
          `seller-listing/v1/items-admin/end-seller-vero-violations`,
          {},
          {},
          {
            'Seller-Account-Id': sellerAccountId,
          },
        );
        return true;
      } catch (err) {
        console.log('scan failed: ', err);
        return false;
      }
    });

    const fetchTemplateList = flow(function* fetchTemplateList() {
      try {
        const { data } = yield self.transport5007.fetch('system/v1/html-templates');
        self.templateList = data;
        self.setDefaultHtmlTemplateId(data[0].id);
      } catch (err) {
        console.log('couldnt fetch system templates: ', err);
      }
    });

    const fetchTemplateById = flow(function* fetchTemplateById(templateId) {
      let templateHTML;
      try {
        const { data } = yield self.transport5007.fetch(`system/v1/html-templates/${templateId}`);
        templateHTML = data.value;
      } catch (err) {
        console.log(`couldn't fetch the template: `, err);
      }
      return templateHTML;
    });

    const fetchListingOptions = flow(function* fetchListingOptions(
      sourceMarketplaceId = getParent(self).markets.defaultSource?.id ?? '',
      sellerAccountCode = getParent(self).markets.defaultSellersAccount?.id ?? '',
      destinationMarketplaceId = getParent(self).markets.defaultDestination?.id ?? '',
    ) {
      setSourceMarketplaceid(sourceMarketplaceId);
      setSellerAccountCode(sellerAccountCode);
      setDestMarketplaceid(destinationMarketplaceId);
      try {
        self.loading = true;
        const { data } = yield self.transport5007.fetch(
          `${URL}?sourceMarketplaceId=${sourceMarketplaceId}&destinationMarketplaceId=${destinationMarketplaceId}`,
          {},
          {
            'Seller-Account-Id': sellerAccountCode,
          },
        );
        data.country = data.country || '';
        self.listingOptions = data;
      } catch (err) {
        console.log('couldnt fetch listing preferences: ', err);
      } finally {
        self.loading = false;
      }
    });

    const updateListingSettings = () => {
      try {
        const headers = {
          'Seller-Account-Id': self.sellerAccountCode,
        };
        const data = {
          ...self.listingOptions,
          sourceMarketplaceId:
            self.listingOptions.sourceMarketplaceId ?? getParent(self).markets.defaultSource.id,
          destinationMarketplaceId:
            self.listingOptions.destinationMarketplaceId ??
            getParent(self).markets.destMarketPlaceId,
        };
        self.transport5007.update(URL, data, {}, headers);
      } catch (err) {
        console.log(err);
      }
    };

    const setEnableSalesTax = bool => {
      self.listingOptions.enableSalesTax = bool;
    };
    const setDefaultHtmlTemplateId = code => {
      self.listingOptions.defaultHtmlTemplateId = code;
    };
    const setMaximalShippingTime = num => {
      if (Number.isInteger(num)) {
        self.listingOptions.maximalShippingTime = num;
      }
    };
    const setMinimalQuantityInStock = num => {
      if (Number.isInteger(num)) {
        self.listingOptions.minimalQuantityInStock = num;
      }
    };
    const setDefaultQuantityToList = num => {
      if (Number.isInteger(num)) {
        self.listingOptions.defaultQuantityToList = num;
      }
    };
    const setCountry = e => {
      let value = e.target ? e.target.value : e;
      if (value.length === 3) {
        value = upperThreeToTwo[value];
      }
      self.listingOptions.country = value;
    };
    const setPostalCode = e => {
      self.listingOptions.postalCode = e.target.value;
    };
    const setCityState = e => {
      self.listingOptions.cityState = e.target.value;
    };

    return {
      setDefaultHtmlTemplateId,
      setMaximalShippingTime,
      setMinimalQuantityInStock,
      setDefaultQuantityToList,
      setEnableSalesTax,
      setSourceMarketplaceid,
      setSellerAccountCode,
      fetchTemplateList,
      fetchTemplateById,
      fetchListingOptions,
      scanUserByToken,
      setCountry,
      setPostalCode,
      setCityState,
      updateTestNumber,
      updateListingSettings,
    };
  });

export default Listing;
